import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

const DialogManagerContext = createContext(null);

let nextDialogId = 0;
let dialogs = [];
const dialogListeners = new Set();
const legacyPropsRegistry = new Map();

const notifyDialogListeners = () => dialogListeners.forEach((listener) => listener(dialogs));
const createDialogId = (prefix = 'dialog') => `${prefix}-${++nextDialogId}`;

const removeDialogs = (predicate) => {
    const nextDialogs = dialogs.filter((dialog) => !predicate(dialog));
    if (nextDialogs.length === dialogs.length) return;
    dialogs = nextDialogs;
    notifyDialogListeners();
};

/** Imperative bridge for Redux thunks and utilities outside React. */
export const openManagedDialog = ({ Component, element, props = {}, channel } = {}) => {
    if (!Component && !element) return undefined;
    if (channel) removeDialogs((dialog) => dialog.channel === channel);

    const id = createDialogId(channel || 'dialog');
    dialogs = [...dialogs, { id, Component, element, props, channel }];
    notifyDialogListeners();
    return id;
};

export const closeManagedDialog = (idOrChannel) => {
    if (!idOrChannel) {
        const topDialog = dialogs[dialogs.length - 1];
        if (topDialog) removeDialogs((dialog) => dialog.id === topDialog.id);
        return;
    }
    removeDialogs((dialog) => dialog.id === idOrChannel || dialog.channel === idOrChannel);
};

export const registerLegacyDialogProps = (props = {}) => {
    const key = createDialogId('legacy-props');
    legacyPropsRegistry.set(key, props);
    return key;
};

export const removeLegacyDialogProps = (key) => {
    if (key) legacyPropsRegistry.delete(key);
};

export const getLegacyDialogProps = (key) => legacyPropsRegistry.get(key) || {};

export const resetManagedDialogs = () => {
    dialogs = [];
    legacyPropsRegistry.clear();
    notifyDialogListeners();
};

export const useLegacyDialogProps = () => {
    const propsKey = useSelector((state) => state.dialog?.propsKey);
    useEffect(() => () => removeLegacyDialogProps(propsKey), [propsKey]);
    return getLegacyDialogProps(propsKey);
};

export const useDialog = () => {
    const context = useContext(DialogManagerContext);
    if (!context) throw new Error('useDialog must be used within DialogManagerProvider');
    return context;
};

export const DialogManagerProvider = ({ children }) => {
    const [renderedDialogs, setRenderedDialogs] = useState(dialogs);

    useEffect(() => {
        const listener = (nextDialogs) => setRenderedDialogs(nextDialogs);
        dialogListeners.add(listener);
        setRenderedDialogs(dialogs);
        return () => {
            dialogListeners.delete(listener);
            resetManagedDialogs();
        };
    }, []);

    const closeDialog = useCallback((id) => closeManagedDialog(id), []);
    const openDialog = useCallback((Component, props = {}) => openManagedDialog({ Component, props }), []);

    return (
        <DialogManagerContext.Provider value={{ openDialog, closeDialog }}>
            {children}
            {renderedDialogs.map(({ id, Component, element, props }) => {
                if (element) return React.cloneElement(element, { key: id });
                return <Component key={id} {...props} onClose={() => closeManagedDialog(id)} />;
            })}
        </DialogManagerContext.Provider>
    );
};
